<!-- Widgets/CampoCliente.vue -->
<template>
  <v-col :cols="cols" :md="md" :sm="sm" :xs="xs">
    <v-autocomplete
        :value="value"
        @input="$emit('input', $event)"
        :search-input.sync="search"
        :items="clientes"
        :loading="carregandoClientes"
        :rules="computedRules"
        :item-text="itemText"
        :item-value="itemValue"
        :label="label"
        :outlined="outlined"
        :dense="dense"
        :hide-details="hideDetails"
        :prepend-inner-icon="prependInnerIcon"
        :clearable="clearable"
        :disabled="disabled"
        :readonly="readonly"
        :placeholder="placeholder"
        no-filter
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
        @change="$emit('change', $event)"
    >
      <template v-slot:no-data>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="mb-2">
              {{ getNoDataMessage() }}
            </v-list-item-title>
            <div v-if="showBuscarTodasFiliais">
              <v-btn
                  class="mt-2"
                  color="primary"
                  text
                  small
                  outlined
                  @click="listarTodosClientes(search)"
                  :loading="carregandoClientes"
              >
                <v-icon left small>mdi-magnify</v-icon>
                Buscar em todas as filiais
              </v-btn>
            </div>
          </v-list-item-content>
        </v-list-item>
      </template>

      <!-- Slot para customizar item -->
      <template v-if="$scopedSlots.item" v-slot:item="data">
        <slot name="item" v-bind="data"></slot>
      </template>

      <!-- Slot para customizar selection -->
      <template v-if="$scopedSlots.selection" v-slot:selection="data">
        <slot name="selection" v-bind="data"></slot>
      </template>
    </v-autocomplete>
  </v-col>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";

export default {
  name: "CampoCliente",

  props: {
    // v-model
    value: {
      type: [String, Number],
      default: null
    },

    // Configurações de layout
    cols: {
      type: [String, Number],
      default: 12
    },
    md: {
      type: [String, Number],
      default: 6
    },
    sm: {
      type: [String, Number],
      default: 12
    },
    xs: {
      type: [String, Number],
      default: 12
    },

    // Configurações do autocomplete
    label: {
      type: String,
      default: "Cliente"
    },
    placeholder: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    itemText: {
      type: String,
      default: "nome"
    },
    itemValue: {
      type: String,
      default: "idcliente"
    },
    outlined: {
      type: Boolean,
      default: true
    },
    dense: {
      type: Boolean,
      default: true
    },
    hideDetails: {
      type: [Boolean, String],
      default: "auto"
    },
    prependInnerIcon: {
      type: String,
      default: "mdi-account"
    },
    clearable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },

    // Configurações de busca
    limit: {
      type: Number,
      default: 30
    },
    searchDelay: {
      type: Number,
      default: null
    },
    idfilial: {
      type: [String, Number],
      default: null
    },
    tipo: {
      type: String,
      default: 'C'
    },
    endpoint: {
      type: String,
      default: 'cliente/listar'
    },
    extraParams: {
      type: Object,
      default: () => ({})
    },

    // Configurações de filial
    enableBuscarTodasFiliais: {
      type: Boolean,
      default: true
    },

    // Mensagens customizadas
    noDataMessage: {
      type: String,
      default: null
    },
    noDataMessageFilial: {
      type: String,
      default: null
    },
    noDataMessageDefault: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      search: null,
      clientes: [],
      carregandoClientes: false,
      aguardarBuscaCliente: null,
      idfilialAtual: null,
    };
  },

  computed: {
    ...mapState(["backendUrl", "timeLimit"]),

    computedRules() {
      let rules = [...this.rules];
      if (this.required) {
        rules.unshift(v => !!v || `${this.label} é obrigatório`);
      }
      return rules;
    },

    searchDelayComputed() {
      return this.searchDelay || this.timeLimit || 500;
    },

    showBuscarTodasFiliais() {
      return (
          this.enableBuscarTodasFiliais &&
          this.search &&
          this.clientes.length === 0 &&
          this.idfilialAtual
      );
    }
  },

  watch: {
    search: {
      handler(val) {
        this.listarClientes(val);
      },
      immediate: false
    },

    idfilial: {
      handler(newVal) {
        this.idfilialAtual = newVal;
        if (this.search) {
          this.listarClientes(this.search);
        }
      },
      immediate: true
    }
  },

  methods: {
    async listarClientes(val) {
      // Limpa timeout anterior para evitar múltiplas requisições
      if (this.aguardarBuscaCliente) {
        clearTimeout(this.aguardarBuscaCliente);
      }

      this.aguardarBuscaCliente = setTimeout(async () => {
        this.carregandoClientes = true;

        try {
          const params = {
            tipo: this.tipo,
            limit: this.limit,
            offset: 0,
            busca: val || null,
            idfilial: this.idfilialAtual || null,
            ...this.extraParams
          };

          const res = await axios.post(`${this.backendUrl}${this.endpoint}`, params);
          this.clientes = res.data?.lista || [];

          // Emite evento com os dados carregados
          this.$emit('clientes-carregados', this.clientes);

        } catch (error) {
          console.error('Erro ao carregar clientes:', error);
          this.clientes = [];
          this.$emit('erro', error);

          // Tenta mostrar snackbar se disponível no parent
          if (this.$parent.mostrarSnackbar) {
            this.$parent.mostrarSnackbar('Erro ao carregar clientes', 'error');
          }
        } finally {
          this.carregandoClientes = false;
        }
      }, this.searchDelayComputed);
    },

    listarTodosClientes(search) {
      this.idfilialAtual = null;
      this.listarClientes(search);
      this.$emit('buscar-todas-filiais', search);
    },

    getNoDataMessage() {
      if (this.noDataMessage) {
        return this.noDataMessage;
      }

      if (!this.search) {
        return this.noDataMessageDefault || 'Digite para buscar clientes';
      }

      if (!this.idfilialAtual) {
        return 'Nenhum resultado encontrado';
      }

      return this.noDataMessageFilial || 'Nenhum resultado encontrado na filial selecionada';
    },


    recarregar() {
      if (this.search) {
        this.listarClientes(this.search);
      }
    },

    limpar() {
      this.search = null;
      this.clientes = [];
    },

    definirFilial(idfilial) {
      this.idfilialAtual = idfilial;
    },

    async definirBusca(termo) {
      await this.listarClientes(termo);
      setTimeout(() => {
        this.value = this.clientes[0]?.idcliente;
      }, 1000);
    }
  },

  beforeDestroy() {
    if (this.aguardarBuscaCliente) {
      clearTimeout(this.aguardarBuscaCliente);
    }
  }
};
</script>